import { ChangeEvent, memo, ReactNode, SyntheticEvent } from 'react';
import cls from 'classnames';

import styles from './Checkbox.module.css';

export const BLACK_COLOR = 'black';
export const WHITE_COLOR = 'white';
export const SPECTRUM_COLOR = 'spectrum';

const TYPE_CLASS_MAP: Record<string, string> = {
  radio: styles.fancyCheckboxRadio,
  checkbox: styles.fancyCheckboxCheckbox,
};

interface CheckboxProps {
  name?: string;
  value?: StringOrNumber;
  onChange?: (e: ChangeEvent<HTMLInputElement>, name?: string) => void;
  onPointerDown?: (e: SyntheticEvent) => void;
  label?: ReactNode;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  checkMarkClassName?: string;
  type?: 'checkbox' | 'radio';
  color?: ValueOf<typeof BLACK_COLOR> | ValueOf<typeof WHITE_COLOR> | ValueOf<typeof SPECTRUM_COLOR>;
  checked?: boolean;
  ambient?: boolean;
  background?: string;
  'aria-label'?: string;
}

function Checkbox(props: CheckboxProps) {
  const {
    type = 'checkbox',
    color = WHITE_COLOR,
    name,
    value,
    label,
    onChange,
    onPointerDown,
    className,
    labelClassName,
    checkMarkClassName,
    disabled,
    ambient,
    background,
    ...rest
  } = props;

  function onClickHandler(e: SyntheticEvent) {
    onChange?.(e as ChangeEvent<HTMLInputElement>, name);
  }

  return (
    // eslint-disable-next-line local/require-aria-label
    <label
      className={cls(
        styles.fancyCheckbox,
        TYPE_CLASS_MAP[type],
        {
          [styles.fancyCheckboxAmbient]: Boolean(ambient),
          [styles.fancyCheckboxBlack]: color === BLACK_COLOR,
          [styles.fancyCheckboxSpectrum]: color === SPECTRUM_COLOR,
          [styles.fancyCheckboxDisabled]: disabled,
        },
        className,
      )}
      onPointerDown={onPointerDown}
    >
      <input disabled={disabled} name={name} onClick={onClickHandler} readOnly type={type} value={value} {...rest} />

      <span className={cls(styles.checkmark, checkMarkClassName)} style={{ backgroundColor: background }}>
        {ambient && <span className={styles.ambientDash} />}
      </span>

      {label && (
        <div
          className={cls(styles.fancyCheckboxLabel, { [styles.fancyCheckboxLabelDisabled]: disabled }, labelClassName)}
        >
          {label}
        </div>
      )}
    </label>
  );
}

export default memo(Checkbox);
